import { apiClient } from '@/api/client';
import type { AuthUser } from '@/types/api';
import type { ApiResponse } from '@/types/api';

export type MobilePlatform = 'android' | 'ios' | 'web';

export interface AppVersionResponse {
  platform: MobilePlatform;
  latestVersion: string;
  minVersion: string;
  forceUpdate: boolean;
  storeUrl: string;
  releaseNotes: string;
}

export interface RegisterDevicePayload {
  platform: MobilePlatform;
  deviceId: string;
  model?: string;
  osVersion?: string;
  appVersion?: string;
  pushToken?: string | null;
  fcmToken?: string | null;
  biometricEnabled?: boolean;
}

export interface CrashReportPayload {
  deviceId?: string;
  platform?: MobilePlatform;
  appVersion?: string;
  osVersion?: string;
  errorMessage: string;
  stackTrace?: string;
  meta?: Record<string, unknown>;
  occurredAt?: string;
}

export interface NotificationPreferences {
  channels: {
    whatsapp: boolean;
    email: boolean;
    push: boolean;
    sms: boolean;
  };
  quietHours?: {
    enabled?: boolean;
    start?: string;
    end?: string;
    timezone?: string;
  };
}

export class MobileApiService {
  static async getAppVersion(platform: MobilePlatform = 'android') {
    const { data } = await apiClient.get<ApiResponse<AppVersionResponse>>(
      '/mobile/app-version',
      { params: { platform } },
    );
    return data;
  }

  static async registerDevice(payload: RegisterDevicePayload) {
    const { data } = await apiClient.post<ApiResponse<unknown>>(
      '/mobile/devices/register',
      payload,
    );
    return data;
  }

  static async updateDeviceToken(
    deviceId: string,
    payload: { pushToken?: string | null; fcmToken?: string | null },
  ) {
    const { data } = await apiClient.put<ApiResponse<unknown>>(
      `/mobile/devices/${encodeURIComponent(deviceId)}/token`,
      payload,
    );
    return data;
  }

  static async listMyDevices() {
    const { data } = await apiClient.get<ApiResponse<unknown[]>>('/mobile/devices/me');
    return data;
  }

  static async unregisterDevice(deviceId: string) {
    const { data } = await apiClient.delete<ApiResponse<unknown>>(
      `/mobile/devices/${encodeURIComponent(deviceId)}`,
    );
    return data;
  }

  static async reportCrash(payload: CrashReportPayload) {
    const { data } = await apiClient.post<ApiResponse<unknown>>(
      '/mobile/crash-reports',
      payload,
    );
    return data;
  }

  static async ackSync(itemIds: string[], deviceId?: string) {
    const { data } = await apiClient.post<ApiResponse<unknown>>('/mobile/sync/ack', {
      itemIds,
      deviceId,
    });
    return data;
  }

  static async getNotificationPreferences() {
    const { data } = await apiClient.get<ApiResponse<NotificationPreferences>>(
      '/mobile/notification-preferences',
    );
    return data;
  }

  static async updateNotificationPreferences(payload: Partial<NotificationPreferences>) {
    const { data } = await apiClient.put<ApiResponse<NotificationPreferences>>(
      '/mobile/notification-preferences',
      payload,
    );
    return data;
  }

  static async updateProfile(payload: {
    firstName: string;
    lastName: string;
    email: string;
    mobile?: string;
  }) {
    const { data } = await apiClient.put<ApiResponse<AuthUser>>('/auth/me', payload);
    return data;
  }
}
